// IOS commands for WAN technologies (Domain 04): serial links, MQC QoS
// (class-map / policy-map / service-policy), IKE/IPsec and tunnel interfaces.
// Same spec shape as ./cli.js: { pattern, kinds, run(s, args) }.
//
// Output is rendered from live state only:
//   serial + tunnel status   → ../net/wan.js
//   QoS counters and rates   → ../net/qos.js (rate model of the lab's traffic)
//   IKE / IPsec SA state     → ../net/wan.js (negotiation rules; no real crypto)

import { ifStatus, l3Addr, linkOf, peerOf } from '../net/sim.js';
import { maskToPrefix } from '../net/addr.js';
import { ifBandwidth, ifDelay } from '../net/routing.js';
import { isSerial, isTunnel, serialEncap, isDce, tunnelState } from '../net/wan.js';
import { qosAnalyze, ifLoad, dscpValue, dscpName, pktsFor, lineRate, APPS } from '../net/qos.js';
import { ifSort, shortIf } from './ifnames.js';
import { macCisco } from '../net/addr.js';

const R = (specs) => specs.map((x) => ({ ...x, kinds: ['router'] }));
const pad = (s, n) => String(s).padEnd(n);
const lpad = (s, n) => String(s).padStart(n);
const INVALID = ["% Invalid input detected at '^' marker."];

export const WAN_SUFFIX = {
  'config-cmap': '(config-cmap)#', 'config-pmap': '(config-pmap)#', 'config-pmap-c': '(config-pmap-c)#',
  'config-isakmp': '(config-isakmp)#', 'config-crypto-trans': '(cfg-crypto-trans)#', 'config-ipsec-profile': '(ipsec-profile)#',
};

// ------------------------------------------------------------------ helpers
const qos = (d) => (d.qos ||= { classMaps: {}, policyMaps: {} });
const crypto = (d) => (d.crypto ||= { isakmp: { policies: {}, keys: [] }, transformSets: {}, profiles: {} });
const curIf = (s) => s.d.interfaces[s.state.ifs[0]];
const onlyIf = (test, fn) => (s, args) => (s.state.ifs.length === 1 && test(s.state.ifs[0]) ? fn(s, args) : INVALID);
const pmap = (s) => s.d.qos?.policyMaps?.[s.state.pmap];
const pclass = (s) => pmap(s)?.classes.find((c) => c.name === s.state.pclass);
const EXITS = (back) => [
  { pattern: 'end', run: (s) => s.endConfig() },
  { pattern: 'do <LINE>', run: (s, [l]) => s.runDo(l) },
  { pattern: 'exit', run: (s) => { s.state.mode = back; return []; } },
];
const CLOCKS = [1200, 2400, 4800, 9600, 19200, 38400, 56000, 64000, 72000, 125000, 128000, 148000, 250000, 500000, 800000, 1000000, 1300000, 2000000, 4000000, 8000000];
const TRANSFORMS = ['esp-aes', 'esp-3des', 'esp-des', 'esp-gcm', 'esp-sha-hmac', 'esp-sha256-hmac', 'esp-md5-hmac', 'ah-sha-hmac'];

function enterPmap(s, name) {
  const q = qos(s.d);
  q.policyMaps[name] ||= { classes: [{ name: 'class-default' }] };
  s.state.pmap = name; s.state.mode = 'config-pmap';
  return [];
}
function enterClass(s, name) {
  const pm = pmap(s);
  if (name !== 'class-default' && !s.d.qos.classMaps[name]) return [`% class-map ${name} not configured`];
  if (!pm.classes.find((c) => c.name === name)) pm.classes.splice(pm.classes.length - 1, 0, { name });
  s.state.pclass = name; s.state.mode = 'config-pmap-c';
  return [];
}
function setRate(s, kind, spec) {
  const c = pclass(s);
  if (kind === 'priority' && c.bandwidth) return ['Must remove the bandwidth command from this class first'];
  if (kind === 'bandwidth' && c.priority) return ['Must remove the priority command from this class first'];
  if (kind === 'bandwidth' && c.name === 'class-default' && false) return [];
  c[kind] = spec;
  return [];
}
function addMatch(s, m, remove = false) {
  const cm = s.d.qos.classMaps[s.state.cmap];
  const same = (x) => x.type === m.type && (x.value || x.values?.join()) === (m.value || m.values?.join());
  cm.matches = cm.matches.filter((x) => !same(x));
  if (!remove) cm.matches.push(m);
  return [];
}
function dscpList(raw) {
  const vals = raw.trim().split(/\s+/);
  if (!vals.length || vals.some((v) => dscpValue(v) === null)) return null;
  return vals.map((v) => v.toLowerCase());
}
/** IOS refuses a policy whose guarantees exceed the interface bandwidth. */
function admit(s, name, ifc) {
  const pm = s.d.qos.policyMaps[name];
  const bw = ifBandwidth(ifc, s.d.interfaces[ifc]);
  let used = 0;
  for (const c of pm.classes) {
    for (const k of ['priority', 'bandwidth']) {
      const sp = c[k];
      if (!sp) continue;
      const v = sp.kbps != null ? sp.kbps : Math.floor((bw * sp.pct) / 100);
      if (used + v > bw) return [`I/f ${ifc} class ${c.name} requested bandwidth ${v} (kbps), available only ${Math.max(0, bw - used)} (kbps)`];
      used += v;
    }
  }
  return null;
}

// ------------------------------------------------------------------ global configuration
export const WAN_CONFIG = R([
  { pattern: 'class-map <WORD>', run: (s, [n]) => enterCmap(s, n, false) },
  { pattern: 'class-map match-all <WORD>', run: (s, [n]) => enterCmap(s, n, false) },
  { pattern: 'class-map match-any <WORD>', run: (s, [n]) => enterCmap(s, n, true) },
  { pattern: 'no class-map <WORD>', run: (s, [n]) => { if (s.d.qos) delete s.d.qos.classMaps[n]; return []; } },
  { pattern: 'policy-map <WORD>', run: (s, [n]) => enterPmap(s, n) },
  { pattern: 'no policy-map <WORD>', run: (s, [n]) => {
    if (!s.d.qos?.policyMaps?.[n]) return [];
    const used = Object.entries(s.d.interfaces).find(([, i]) => i.servicePolicy?.input === n || i.servicePolicy?.output === n);
    if (used) return [`Policy map ${n} is being used`];
    delete s.d.qos.policyMaps[n]; return [];
  } },
  { pattern: 'crypto isakmp policy <1-10000>', run: (s, [p]) => { crypto(s.d).isakmp.policies[p] ||= { encr: 'des', hash: 'sha', auth: 'rsa-sig', group: 1, lifetime: 86400 }; s.state.isakmp = p; s.state.mode = 'config-isakmp'; return []; } },
  { pattern: 'no crypto isakmp policy <1-10000>', run: (s, [p]) => { if (s.d.crypto) delete s.d.crypto.isakmp.policies[p]; return []; } },
  { pattern: 'crypto isakmp key <WORD> address <A.B.C.D>', run: (s, [k, a]) => { const c = crypto(s.d); c.isakmp.keys = c.isakmp.keys.filter((x) => x.address !== a); c.isakmp.keys.push({ key: k, address: a }); return []; } },
  { pattern: 'no crypto isakmp key <WORD> address <A.B.C.D>', run: (s, [, a]) => { if (s.d.crypto) s.d.crypto.isakmp.keys = s.d.crypto.isakmp.keys.filter((x) => x.address !== a); return []; } },
  { pattern: 'crypto ipsec transform-set <WORD> <LINE>', run: (s, [n, t]) => transformSet(s, n, t) },
  { pattern: 'no crypto ipsec transform-set <WORD>', run: (s, [n]) => { if (s.d.crypto) delete s.d.crypto.transformSets[n]; return []; } },
  { pattern: 'crypto ipsec profile <WORD>', run: (s, [n]) => { crypto(s.d).profiles[n] ||= { transformSet: null }; s.state.profile = n; s.state.mode = 'config-ipsec-profile'; return []; } },
  { pattern: 'no crypto ipsec profile <WORD>', run: (s, [n]) => { if (s.d.crypto) delete s.d.crypto.profiles[n]; return []; } },
]);
function enterCmap(s, name, any) {
  const q = qos(s.d);
  q.classMaps[name] ||= { matchAny: any, matches: [] };
  q.classMaps[name].matchAny = any;
  s.state.cmap = name; s.state.mode = 'config-cmap';
  return [];
}
function transformSet(s, name, text) {
  const toks = text.trim().toLowerCase().split(/\s+/);
  const out = [];
  for (let k = 0; k < toks.length; k++) {
    if (!TRANSFORMS.includes(toks[k])) return INVALID;
    if (/^esp-aes$/.test(toks[k]) && /^(128|192|256)$/.test(toks[k + 1] || '')) { out.push(`esp-aes ${toks[k + 1]}`); k++; } else out.push(toks[k]);
  }
  if (!out.length || out.length > 3) return INVALID;
  crypto(s.d).transformSets[name] = { transforms: out, mode: 'tunnel' };
  s.state.tset = name; s.state.mode = 'config-crypto-trans';
  return [];
}

// ------------------------------------------------------------------ sub-modes
const CMAP = [
  ...EXITS('config'),
  { pattern: 'match dscp <LINE>', run: (s, [v]) => { const l = dscpList(v); return l ? addMatch(s, { type: 'dscp', values: l }) : INVALID; } },
  { pattern: 'match ip dscp <LINE>', run: (s, [v]) => { const l = dscpList(v); return l ? addMatch(s, { type: 'dscp', values: l }) : INVALID; } },
  { pattern: 'no match dscp <LINE>', run: (s, [v]) => { const l = dscpList(v); return l ? addMatch(s, { type: 'dscp', values: l }, true) : INVALID; } },
  { pattern: 'no match ip dscp <LINE>', run: (s, [v]) => { const l = dscpList(v); return l ? addMatch(s, { type: 'dscp', values: l }, true) : INVALID; } },
  { pattern: 'match protocol <WORD>', run: (s, [p]) => (APPS.includes(p.toLowerCase()) ? addMatch(s, { type: 'protocol', value: p.toLowerCase() }) : INVALID) },
  { pattern: 'no match protocol <WORD>', run: (s, [p]) => addMatch(s, { type: 'protocol', value: p.toLowerCase() }, true) },
  { pattern: 'match any', run: (s) => addMatch(s, { type: 'any', value: 'any' }) },
  { pattern: 'description <LINE>', run: (s, [t]) => { s.d.qos.classMaps[s.state.cmap].description = t; return []; } },
];
const PMAP = [
  ...EXITS('config'),
  { pattern: 'class <WORD>', run: (s, [n]) => enterClass(s, n) },
  { pattern: 'no class <WORD>', run: (s, [n]) => { const pm = pmap(s); if (n !== 'class-default') pm.classes = pm.classes.filter((c) => c.name !== n); return []; } },
  { pattern: 'description <LINE>', run: (s, [t]) => { pmap(s).description = t; return []; } },
];
const PMAPC = [
  ...EXITS('config-pmap'),
  { pattern: 'class <WORD>', run: (s, [n]) => enterClass(s, n) },
  { pattern: 'priority <8-2000000>', run: (s, [k]) => setRate(s, 'priority', { kbps: +k }) },
  { pattern: 'priority percent <1-100>', run: (s, [p]) => setRate(s, 'priority', { pct: +p }) },
  { pattern: 'no priority', run: (s) => { delete pclass(s).priority; return []; } },
  { pattern: 'bandwidth <8-2000000>', run: (s, [k]) => setRate(s, 'bandwidth', { kbps: +k }) },
  { pattern: 'bandwidth percent <1-100>', run: (s, [p]) => setRate(s, 'bandwidth', { pct: +p }) },
  { pattern: 'no bandwidth', run: (s) => { delete pclass(s).bandwidth; return []; } },
  { pattern: 'set dscp <WORD>', run: (s, [v]) => (dscpValue(v) === null ? INVALID : ((pclass(s).set = { dscp: v.toLowerCase() }), [])) },
  { pattern: 'set ip dscp <WORD>', run: (s, [v]) => (dscpValue(v) === null ? INVALID : ((pclass(s).set = { dscp: v.toLowerCase() }), [])) },
  { pattern: 'no set dscp', run: (s) => { delete pclass(s).set; return []; } },
  { pattern: 'no set ip dscp', run: (s) => { delete pclass(s).set; return []; } },
  { pattern: 'fair-queue', run: (s) => { pclass(s).fairQueue = true; return []; } },
  { pattern: 'no fair-queue', run: (s) => { delete pclass(s).fairQueue; return []; } },
];
const ISAKMP = [
  ...EXITS('config'),
  ...[['aes', 'aes'], ['aes 256', 'aes 256'], ['3des', '3des'], ['des', 'des']].map(([p, v]) => ({ pattern: `encryption ${p}`, run: (s) => { s.d.crypto.isakmp.policies[s.state.isakmp].encr = v; return []; } })),
  ...['sha', 'sha256', 'md5'].map((v) => ({ pattern: `hash ${v}`, run: (s) => { s.d.crypto.isakmp.policies[s.state.isakmp].hash = v; return []; } })),
  { pattern: 'authentication pre-share', run: (s) => { s.d.crypto.isakmp.policies[s.state.isakmp].auth = 'pre-share'; return []; } },
  { pattern: 'authentication rsa-sig', run: (s) => { s.d.crypto.isakmp.policies[s.state.isakmp].auth = 'rsa-sig'; return []; } },
  { pattern: 'group <1-24>', run: (s, [g]) => { s.d.crypto.isakmp.policies[s.state.isakmp].group = +g; return []; } },
  { pattern: 'lifetime <60-86400>', run: (s, [l]) => { s.d.crypto.isakmp.policies[s.state.isakmp].lifetime = +l; return []; } },
];
const TRANS = [
  ...EXITS('config'),
  { pattern: 'mode tunnel', run: (s) => { s.d.crypto.transformSets[s.state.tset].mode = 'tunnel'; return []; } },
  { pattern: 'mode transport', run: (s) => { s.d.crypto.transformSets[s.state.tset].mode = 'transport'; return []; } },
];
const PROFILE = [
  ...EXITS('config'),
  { pattern: 'set transform-set <WORD>', run: (s, [t]) => { s.d.crypto.profiles[s.state.profile].transformSet = t; return []; } },
  { pattern: 'no set transform-set', run: (s) => { s.d.crypto.profiles[s.state.profile].transformSet = null; return []; } },
];
export const WAN_MODE_SPECS = {
  'config-cmap': R(CMAP), 'config-pmap': R(PMAP), 'config-pmap-c': R(PMAPC),
  'config-isakmp': R(ISAKMP), 'config-crypto-trans': R(TRANS), 'config-ipsec-profile': R(PROFILE),
};

// ------------------------------------------------------------------ interface configuration
const tun = (s) => (curIf(s).tunnel ||= { source: null, destination: null, mode: 'gre', protection: null });
export const WAN_IF = R([
  { pattern: 'encapsulation ppp', run: onlyIf(isSerial, (s) => { curIf(s).wanEncap = 'ppp'; return []; }) },
  { pattern: 'encapsulation hdlc', run: onlyIf(isSerial, (s) => { curIf(s).wanEncap = 'hdlc'; return []; }) },
  { pattern: 'clock rate <WORD>', run: onlyIf(isSerial, (s, [r]) => {
    if (!CLOCKS.includes(+r)) return ['%Error: Invalid clock rate', `Valid values: ${CLOCKS.slice(0, 6).join(', ')} ... 8000000`];
    if (!isDce(s.net, s.id, s.state.ifs[0])) return ['%Error: This command applies only to DCE interfaces'];
    curIf(s).clockRate = +r; return [];
  }) },
  { pattern: 'no clock rate', run: onlyIf(isSerial, (s) => { delete curIf(s).clockRate; return []; }) },
  { pattern: 'service-policy input <WORD>', run: (s, [n]) => servicePolicy(s, 'input', n) },
  { pattern: 'service-policy output <WORD>', run: (s, [n]) => servicePolicy(s, 'output', n) },
  { pattern: 'no service-policy input <WORD>', run: (s) => { const sp = curIf(s).servicePolicy; if (sp) delete sp.input; return []; } },
  { pattern: 'no service-policy output <WORD>', run: (s) => { const sp = curIf(s).servicePolicy; if (sp) delete sp.output; return []; } },
  { pattern: 'tunnel source <IF>', run: onlyIf(isTunnel, (s, [i]) => { tun(s).source = s.ifn(i); return []; }) },
  { pattern: 'tunnel source <A.B.C.D>', run: onlyIf(isTunnel, (s, [a]) => { tun(s).source = a; return []; }) },
  { pattern: 'no tunnel source', run: onlyIf(isTunnel, (s) => { tun(s).source = null; return []; }) },
  { pattern: 'tunnel destination <A.B.C.D>', run: onlyIf(isTunnel, (s, [a]) => { tun(s).destination = a; return []; }) },
  { pattern: 'no tunnel destination', run: onlyIf(isTunnel, (s) => { tun(s).destination = null; return []; }) },
  { pattern: 'tunnel mode ipsec ipv4', run: onlyIf(isTunnel, (s) => { tun(s).mode = 'ipsec'; return []; }) },
  { pattern: 'tunnel mode gre ip', run: onlyIf(isTunnel, (s) => { tun(s).mode = 'gre'; return []; }) },
  { pattern: 'tunnel protection ipsec profile <WORD>', run: onlyIf(isTunnel, (s, [p]) => {
    if (!s.d.crypto?.profiles?.[p]) return [`% ipsec profile ${p} not found`];
    tun(s).protection = p; return [];
  }) },
  { pattern: 'no tunnel protection ipsec profile <WORD>', run: onlyIf(isTunnel, (s) => { tun(s).protection = null; return []; }) },
]);
function servicePolicy(s, dir, name) {
  if (s.state.ifs.length !== 1) return INVALID;
  if (!s.d.qos?.policyMaps?.[name]) return [`policy map ${name} not configured`];
  const ifc = s.state.ifs[0];
  const i = curIf(s);
  if (dir === 'output') { const err = admit(s, name, ifc); if (err) return err; }
  if (dir === 'input' && s.d.qos.policyMaps[name].classes.some((c) => c.priority || c.bandwidth)) return ['  CBWFQ : Not supported in the input direction'];
  i.servicePolicy = { ...(i.servicePolicy || {}), [dir]: name };
  return [];
}

// ------------------------------------------------------------------ show
const matchText = (m) => (m.type === 'dscp' ? `dscp ${m.values.map((v) => `${v} (${dscpValue(v)})`).join(' ')}` : m.type === 'protocol' ? `protocol ${m.value}` : 'any');
function policyConfig(d, name) {
  const pm = d.qos.policyMaps[name];
  const out = [`  Policy Map ${name}`];
  for (const c of pm.classes) {
    out.push(`    Class ${c.name}`);
    if (c.priority) out.push(c.priority.kbps != null ? `      priority ${c.priority.kbps} (kbps)` : `      priority ${c.priority.pct} (%)`);
    if (c.bandwidth) out.push(c.bandwidth.kbps != null ? `      bandwidth ${c.bandwidth.kbps} (kbps)` : `      bandwidth ${c.bandwidth.pct} (%)`);
    if (c.set) out.push(`      set dscp ${c.set.dscp}`);
    if (c.fairQueue) out.push('      fair-queue');
  }
  return out;
}
function policyInterface(net, id, n) {
  const d = net.devices[id];
  const sp = d.interfaces[n]?.servicePolicy || {};
  if (!sp.input && !sp.output) return [];
  const q = qosAnalyze(net);
  const out = [` ${n}`, ''];
  const st = ifStatus(net, id, n);
  for (const dir of ['input', 'output']) {
    const name = sp[dir];
    if (!name || !d.qos?.policyMaps?.[name]) continue;
    out.push(`  Service-policy ${dir}: ${name}`, '');
    const stats = dir === 'output' ? q.ifs[`${id}|${n}`] : q.inputs[`${id}|${n}`];
    for (const c of d.qos.policyMaps[name].classes) {
      const cm = d.qos.classMaps?.[c.name];
      const cs = stats?.classes?.[c.name] || { offered: 0, served: 0, drop: 0 };
      const live = st.protocol === 'up';
      const off = live ? cs.offered : 0; const drop = live ? (cs.drop || 0) : 0;
      const size = c.name === 'class-default' ? 1000 : (cm?.matches || []).some((m) => m.type === 'protocol' && m.value === 'rtp') || (cm?.matches || []).some((m) => m.type === 'dscp' && m.values.includes('ef')) ? 200 : 1000;
      out.push(`    Class-map: ${c.name} (${c.name === 'class-default' ? 'match-any' : cm?.matchAny ? 'match-any' : 'match-all'})`);
      out.push(`      ${pktsFor(off, size)} packets, ${pktsFor(off, size) * size} bytes`);
      out.push(`      30 second offered rate ${Math.round(off * 1000)} bps, drop rate ${Math.round(drop * 1000)} bps`);
      if (c.name === 'class-default') out.push('      Match: any');
      else for (const m of cm?.matches || []) out.push(`      Match: ${matchText(m)}`);
      if (c.priority) {
        const kb = c.priority.kbps != null ? c.priority.kbps : Math.floor((ifBandwidth(n, d.interfaces[n]) * c.priority.pct) / 100);
        out.push('      Priority: Strict, b/w exceed drops: ' + pktsFor(drop, size), '', `      Priority: ${kb} kbps, burst bytes ${Math.max(1500, Math.round(kb * 25))}`);
      }
      if (c.bandwidth || (dir === 'output' && !c.priority)) {
        out.push('      Queueing', `      queue limit 64 packets`, `      (queue depth/total drops/no-buffer drops) ${drop > 0 ? 64 : 0}/${pktsFor(drop, size)}/0`, `      (pkts output/bytes output) ${pktsFor(off - drop, size)}/${pktsFor(off - drop, size) * size}`);
        if (c.bandwidth) out.push(c.bandwidth.kbps != null ? `      bandwidth ${c.bandwidth.kbps} kbps` : `      bandwidth ${c.bandwidth.pct}% (${Math.floor((ifBandwidth(n, d.interfaces[n]) * c.bandwidth.pct) / 100)} kbps)`);
        if (c.fairQueue) out.push('      Fair-queue: per-flow queue limit 16 packets');
      }
      if (c.set) out.push('      QoS Set', `        dscp ${c.set.dscp}`, `          Packets marked ${pktsFor(off, size)}`);
      out.push('');
    }
  }
  return out;
}

export const WAN_SHOW = R([
  { pattern: 'show class-map', run: (s) => {
    const cms = s.d.qos?.classMaps || {};
    const out = [' Class Map match-any class-default (id 0)', '   Match any', ''];
    Object.entries(cms).forEach(([n, cm], k) => { out.push(` Class Map ${cm.matchAny ? 'match-any' : 'match-all'} ${n} (id ${k + 1})`); for (const m of cm.matches) out.push(`   Match ${matchText(m)}`); out.push(''); });
    return out;
  } },
  { pattern: 'show policy-map', run: (s) => Object.keys(s.d.qos?.policyMaps || {}).flatMap((n) => [...policyConfig(s.d, n), '']) },
  { pattern: 'show policy-map <WORD>', run: (s, [n]) => (s.d.qos?.policyMaps?.[n] ? policyConfig(s.d, n) : [`% Policy map ${n} not configured`]) },
  { pattern: 'show policy-map interface', run: (s) => Object.keys(s.d.interfaces).sort(ifSort).flatMap((n) => policyInterface(s.net, s.id, n)) },
  { pattern: 'show policy-map interface <IF>', run: (s, [i]) => policyInterface(s.net, s.id, s.ifn(i)) },
  { pattern: 'show controllers <IF>', run: (s, [i]) => controllers(s.net, s.id, s.ifn(i)) },
  { pattern: 'show crypto isakmp sa', run: (s) => isakmpSa(s.net, s.id) },
  { pattern: 'show crypto isakmp policy', run: (s) => isakmpPolicy(s.d) },
  { pattern: 'show crypto ipsec sa', run: (s) => ipsecSa(s.net, s.id) },
  { pattern: 'show crypto session', run: (s) => cryptoSession(s.net, s.id) },
  { pattern: 'show crypto ipsec transform-set', run: (s) => Object.entries(s.d.crypto?.transformSets || {}).flatMap(([n, t]) => [`Transform set ${n}: { ${t.transforms.join(' ')} }`, `   will negotiate = { ${t.mode === 'tunnel' ? 'Tunnel' : 'Transport'},  },`, '']) },
]);

function controllers(net, id, n) {
  if (!n) return INVALID;
  if (!isSerial(n)) return [`Interface ${n}`, '  Hardware is CN Gigabit Ethernet', `  HWIDB: ${n}`];
  const l = linkOf(net, id, n);
  const i = net.devices[id].interfaces[n];
  const out = [`Interface ${n}`, 'Hardware is SCC'];
  if (!l) out.push('No serial cable attached');
  else if (isDce(net, id, n)) out.push(`DCE V.35, clock rate ${i.clockRate || 2000000}`);
  else out.push('DTE V.35 TX and RX clocks detected.');
  out.push('idb at 0x3E1A3B24, driver data structure at 0x3E1A5F04', 'SCC Registers:', 'General [GSMR]=0x2:0x00000030, Protocol-specific [PSMR]=0x8', 'Events [SCCE]=0x0000, Mask [SCCM]=0x001F, Status [SCCS]=0x06');
  return out;
}

/** `show interfaces SerialX` */
export function serialInterface(net, id, n) {
  const d = net.devices[id]; const i = d.interfaces[n];
  const st = ifStatus(net, id, n);
  const enc = serialEncap(i);
  const ld = ifLoad(net, id, n);
  const head = st.status === 'administratively down' ? `${n} is administratively down, line protocol is down` : `${n} is ${st.status}, line protocol is ${st.protocol}`;
  const out = [head, '  Hardware is WIC MBRD Serial'];
  if (i.description) out.push(`  Description: ${i.description}`);
  if (i.ip) out.push(`  Internet address is ${i.ip.ip}/${maskToPrefix(i.ip.mask)}`);
  out.push(`  MTU 1500 bytes, BW ${ifBandwidth(n, i)} Kbit/sec, DLY ${ifDelay(n, i) * 10} usec,`,
    `     reliability 255/255, txload ${ld ? ld.txload : 1}/255, rxload ${ld ? ld.rxload : 1}/255`);
  if (enc === 'ppp') out.push(`  Encapsulation PPP, LCP ${st.protocol === 'up' ? 'Open' : st.status === 'up' ? 'REQsent' : 'Closed'}`, `  Open: ${st.protocol === 'up' ? 'IPCP, CDPCP' : ''}, loopback not set`);
  else out.push('  Encapsulation HDLC, loopback not set');
  out.push('  Keepalive set (10 sec)', `  Last input ${st.protocol === 'up' ? '00:00:02' : st.status === 'up' ? '00:00:41' : 'never'}, output 00:00:01, output hang never`,
    '  Last clearing of "show interface" counters never',
    `  Input queue: 0/75/0/0 (size/max/drops/flushes); Total output drops: ${ld ? pktsFor(ld.drops) : 0}`,
    `  Queueing strategy: ${ld?.policy || i.servicePolicy?.output ? 'Class-based queueing' : 'fifo'}`,
    ld?.policy || i.servicePolicy?.output ? `  Output queue: ${ld?.congested ? 64 : 0}/1000/64/${ld ? pktsFor(ld.drops) : 0} (size/max total/threshold/drops)` : `  Output queue: ${ld?.congested ? 40 : 0}/40 (size/max)`,
    `  5 minute input rate ${ld ? Math.round(ld.rxKbps * 1000) : 0} bits/sec, ${ld ? Math.round(ld.rxKbps / 2) : 0} packets/sec`,
    `  5 minute output rate ${ld ? Math.round(ld.txKbps * 1000) : 0} bits/sec, ${ld ? Math.round(ld.txKbps / 2) : 0} packets/sec`,
    `     ${i.counters.inPkts} packets input, ${i.counters.inPkts * 98} bytes, 0 no buffer`,
    '     Received 0 broadcasts (0 IP multicasts)',
    '     0 runts, 0 giants, 0 throttles',
    '     0 input errors, 0 CRC, 0 frame, 0 overrun, 0 ignored, 0 abort',
    `     ${i.counters.outPkts} packets output, ${i.counters.outPkts * 98} bytes, 0 underruns`,
    `     0 output errors, 0 collisions, ${st.protocol === 'up' ? 1 : 3} interface resets`,
    '     0 unknown protocol drops',
    '     0 output buffer failures, 0 output buffers swapped out',
    `     ${st.status === 'up' && st.protocol === 'down' ? 2 : 0} carrier transitions`);
  const cable = linkOf(net, id, n);
  out.push(st.status === 'up' || st.status === 'down' ? `     DCD=${cable && st.status === 'up' ? 'up' : 'down'}  DSR=${cable ? 'up' : 'down'}  DTR=up  RTS=up  CTS=${cable ? 'up' : 'down'}` : '     DCD=down  DSR=down  DTR=down  RTS=down  CTS=down');
  return out;
}

/** `show interfaces TunnelX` */
export function tunnelInterface(net, id, n) {
  const d = net.devices[id]; const i = d.interfaces[n];
  const st = ifStatus(net, id, n);
  const t = i.tunnel || {};
  const ts = tunnelState(net)[`${id}|${n}`] || {};
  const head = st.status === 'administratively down' ? `${n} is administratively down, line protocol is down` : `${n} is ${st.status}, line protocol is ${st.protocol}`;
  const out = [head, '  Hardware is Tunnel'];
  if (i.description) out.push(`  Description: ${i.description}`);
  if (i.ip) out.push(`  Internet address is ${i.ip.ip}/${maskToPrefix(i.ip.mask)}`);
  const srcIfc = t.source && !/^\d/.test(t.source) ? ` (${t.source})` : '';
  out.push(`  MTU 17862 bytes, BW 100 Kbit/sec, DLY 50000 usec,`, '     reliability 255/255, txload 1/255, rxload 1/255', '  Encapsulation TUNNEL, loopback not set', '  Keepalive not set',
    `  Tunnel linestate evaluation ${st.protocol === 'up' ? 'up' : 'down'}${st.protocol !== 'up' && ts.reason ? ` - ${({ source: 'transport source is down', destination: 'no destination configured', 'no-route': 'no route to tunnel destination', shutdown: 'interface is administratively down' })[ts.reason] || 'IPsec SA not established'}` : ''}`,
    `  Tunnel source ${ts.src || (t.source && /^\d/.test(t.source) ? t.source : 'UNKNOWN')}${srcIfc}, destination ${t.destination || 'UNKNOWN'}`,
    `   Tunnel Subblocks:`,
    `  Tunnel protocol/transport ${t.mode === 'ipsec' ? 'IPSEC/IP' : 'GRE/IP'}`);
  if (t.protection) out.push(`  Tunnel protection via IPSec (profile "${t.protection}")`);
  out.push('  Last input never, output never, output hang never', '  Queueing strategy: fifo',
    `     ${i.counters.inPkts} packets input, ${i.counters.inPkts * 84} bytes, 0 no buffer`,
    `     ${i.counters.outPkts} packets output, ${i.counters.outPkts * 84} bytes, 0 underruns`);
  return out;
}

function isakmpSa(net, id) {
  const out = ['IPv4 Crypto ISAKMP SA', 'dst             src             state          conn-id status'];
  let k = 1001;
  for (const [key, t] of Object.entries(tunnelState(net))) {
    if (!key.startsWith(`${id}|`) || t.mode !== 'ipsec' || !t.ike) continue;
    out.push(`${pad(t.dst, 16)}${pad(t.src, 16)}${pad(t.ike, 15)}${lpad(k++, 7)} ${t.ike === 'QM_IDLE' ? 'ACTIVE' : t.ike === 'MM_KEY_EXCH' ? 'ACTIVE' : 'ACTIVE'}`);
  }
  out.push('', 'IPv6 Crypto ISAKMP SA', '');
  return out;
}
function isakmpPolicy(d) {
  const out = ['', 'Global IKE policy'];
  for (const [p, v] of Object.entries(d.crypto?.isakmp?.policies || {})) {
    const enc = { aes: 'AES - Advanced Encryption Standard (128 bit keys).', 'aes 256': 'AES - Advanced Encryption Standard (256 bit keys).', '3des': 'Three key triple DES', des: 'DES - Data Encryption Standard (56 bit keys).' }[v.encr];
    out.push(`Protection suite of priority ${p}`, `        encryption algorithm:   ${enc}`, `        hash algorithm:         ${v.hash === 'sha' ? 'Secure Hash Standard' : v.hash === 'sha256' ? 'Secure Hash Standard 2 (256 bit)' : 'Message Digest 5'}`,
      `        authentication method:  ${v.auth === 'pre-share' ? 'Pre-Shared Key' : 'Rivest-Shamir-Adleman Signature'}`, `        Diffie-Hellman group:   #${v.group} (${{ 1: 768, 2: 1024, 5: 1536, 14: 2048, 15: 3072, 16: 4096, 19: 256, 20: 384, 21: 521, 24: 2048 }[v.group] || 2048} bit)`, `        lifetime:               ${v.lifetime} seconds, no volume limit`);
  }
  return out;
}
function ipsecSa(net, id) {
  const out = [];
  const d = net.devices[id];
  for (const [key, t] of Object.entries(tunnelState(net))) {
    if (!key.startsWith(`${id}|`) || t.mode !== 'ipsec') continue;
    const ifc = key.split('|')[1];
    const i = d.interfaces[ifc];
    const up = t.ipsec;
    const enc = up ? i.counters.outPkts : 0; const dec = up ? i.counters.inPkts : 0;
    const ts = d.crypto?.profiles?.[i.tunnel?.protection]?.transformSet;
    const tf = d.crypto?.transformSets?.[ts]?.transforms?.join(' ') || 'none';
    out.push(`interface: ${ifc}`, `    Crypto map tag: ${ifc}-head-0, local addr ${t.src || '0.0.0.0'}`, '', '   protected vrf: (none)',
      '   local  ident (addr/mask/prot/port): (0.0.0.0/0.0.0.0/0/0)', '   remote ident (addr/mask/prot/port): (0.0.0.0/0.0.0.0/0/0)',
      `   current_peer ${t.dst || '0.0.0.0'} port 500`, '     PERMIT, flags={origin_is_acl,}',
      `    #pkts encaps: ${enc}, #pkts encrypt: ${enc}, #pkts digest: ${enc}`, `    #pkts decaps: ${dec}, #pkts decrypt: ${dec}, #pkts verify: ${dec}`,
      '    #pkts compressed: 0, #pkts decompressed: 0', `    #send errors ${up ? 0 : enc}, #recv errors 0`, '',
      `     local crypto endpt.: ${t.src || '0.0.0.0'}, remote crypto endpt.: ${t.dst || '0.0.0.0'}`,
      `     plaintext mtu 1438, path mtu 1500, ip mtu 1500, ip mtu idb ${i.tunnel?.source || 'unknown'}`,
      `     current outbound spi: ${up ? '(simulated SA)' : '0x0(0)'}`, `     PFS (Y/N): N, DH group: none`, '',
      '     inbound esp sas:', ...(up ? [`      spi: (simulated SA)`, `        transform: ${tf} ,`, '        in use settings ={Tunnel, }', '        status: ACTIVE'] : []),
      '', '     outbound esp sas:', ...(up ? [`      spi: (simulated SA)`, `        transform: ${tf} ,`, '        in use settings ={Tunnel, }', '        status: ACTIVE'] : []), '');
  }
  return out.length ? out : ['', 'No SAs found', ''];
}
function cryptoSession(net, id) {
  const out = ['Crypto session current status', '', 'Interface: Tunnel'];
  out.length = 1; out.push('');
  for (const [key, t] of Object.entries(tunnelState(net))) {
    if (!key.startsWith(`${id}|`) || t.mode !== 'ipsec') continue;
    const status = t.ipsec ? 'UP-ACTIVE' : t.ike === 'QM_IDLE' ? 'UP-NO-IPSEC' : 'DOWN';
    out.push(`Interface: ${key.split('|')[1]}`, `Session status: ${status}`, `Peer: ${t.dst || '0.0.0.0'} port 500`,
      ...(t.ike === 'QM_IDLE' ? [`  Session ID: 0`, `  IKEv1 SA: local ${t.src}/500 remote ${t.dst}/500 Active`] : []),
      ...(t.ipsec ? ['  IPSEC FLOW: permit ip 0.0.0.0/0.0.0.0 0.0.0.0/0.0.0.0', '        Active SAs: 2, origin: crypto map'] : []), '');
  }
  return out;
}

// ------------------------------------------------------------------ running-config
export function wanConfigLines(d) {
  const out = [];
  const c = d.crypto;
  if (c) {
    for (const [p, v] of Object.entries(c.isakmp?.policies || {})) {
      out.push(`crypto isakmp policy ${p}`);
      if (v.encr !== 'des') out.push(` encr ${v.encr}`);
      if (v.hash !== 'sha') out.push(` hash ${v.hash}`);
      if (v.auth === 'pre-share') out.push(' authentication pre-share');
      if (v.group !== 1) out.push(` group ${v.group}`);
      if (v.lifetime !== 86400) out.push(` lifetime ${v.lifetime}`);
    }
    for (const k of c.isakmp?.keys || []) out.push(`crypto isakmp key ${k.key} address ${k.address}`);
    if (Object.keys(c.isakmp?.policies || {}).length || c.isakmp?.keys?.length) out.push('!');
    for (const [n, t] of Object.entries(c.transformSets || {})) { out.push(`crypto ipsec transform-set ${n} ${t.transforms.join(' ')}`, ` mode ${t.mode}`); }
    if (Object.keys(c.transformSets || {}).length) out.push('!');
    for (const [n, p] of Object.entries(c.profiles || {})) { out.push(`crypto ipsec profile ${n}`); if (p.transformSet) out.push(` set transform-set ${p.transformSet}`); out.push('!'); }
  }
  const q = d.qos;
  if (q) {
    for (const [n, cm] of Object.entries(q.classMaps || {})) {
      out.push(`class-map ${cm.matchAny ? 'match-any' : 'match-all'} ${n}`);
      for (const m of cm.matches) out.push(` match ${m.type === 'dscp' ? `dscp ${m.values.join(' ')}` : m.type === 'protocol' ? `protocol ${m.value}` : 'any'}`);
    }
    if (Object.keys(q.classMaps || {}).length) out.push('!');
    for (const [n, pm] of Object.entries(q.policyMaps || {})) {
      out.push(`policy-map ${n}`);
      for (const c2 of pm.classes) {
        const lines = [];
        if (c2.priority) lines.push(c2.priority.kbps != null ? `  priority ${c2.priority.kbps}` : `  priority percent ${c2.priority.pct}`);
        if (c2.bandwidth) lines.push(c2.bandwidth.kbps != null ? `  bandwidth ${c2.bandwidth.kbps}` : `  bandwidth percent ${c2.bandwidth.pct}`);
        if (c2.set) lines.push(`  set dscp ${c2.set.dscp}`);
        if (c2.fairQueue) lines.push('  fair-queue');
        if (c2.name === 'class-default' && !lines.length) continue;
        out.push(` class ${c2.name}`, ...lines);
      }
      out.push('!');
    }
  }
  return out;
}
export function wanIfLines(n, i) {
  const out = [];
  if (i.tunnel && isTunnel(n)) {
    if (i.tunnel.source) out.push(` tunnel source ${i.tunnel.source}`);
    if (i.tunnel.destination) out.push(` tunnel destination ${i.tunnel.destination}`);
    if (i.tunnel.mode === 'ipsec') out.push(' tunnel mode ipsec ipv4');
    if (i.tunnel.protection) out.push(` tunnel protection ipsec profile ${i.tunnel.protection}`);
  }
  if (isSerial(n)) {
    if (i.wanEncap === 'ppp') out.push(' encapsulation ppp');
    if (i.clockRate) out.push(` clock rate ${i.clockRate}`);
  }
  if (i.servicePolicy?.input) out.push(` service-policy input ${i.servicePolicy.input}`);
  if (i.servicePolicy?.output) out.push(` service-policy output ${i.servicePolicy.output}`);
  return out;
}
export { isSerial, isTunnel, shortIf, macCisco, l3Addr, peerOf, lineRate };
